/**
 * The manuscript's left margin: one slot per paragraph holding either an add button or a note
 * marker, plus the expanded note card when the window is wide enough for one.
 *
 * Everything here lives OUTSIDE the contentEditable. Inside it, cards would be editable text,
 * would be counted as paragraphs by the minimap and by fixFormatting, and would be serialized
 * into story_content by handleInput.
 *
 * Positions are measured, not styled: the gutter's width depends on the chat panel (user-draggable
 * 280px to 50vw) and the sidebar, neither of which a media query can see.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Sparkles, EyeOff, PenLine, Asterisk } from 'lucide-react'
import {
  MANUSCRIPT_PARAGRAPH_SELECTOR,
  anchorIdOf,
  createAnchorId,
  isAnchorableBlock,
  scanAnchorableBlocks
} from '../lib/paragraphAnchors'

/** Width of the expanded card. Below this much gutter, the marker is the whole presentation. */
const CARD_WIDTH = 200
/** Gap between the card's right edge and the first character of prose. */
const CARD_GAP = 32
/** The minimap rail owns x in [10, 34]; the card must clear it by 8px. */
const MINIMAP_CLEARANCE = 42
/** Glyph box: 20px wide, sitting 6px left of the prose. */
const GLYPH_WIDTH = 20
const GLYPH_GAP = 6
/** Hysteresis, so dragging the chat panel across the boundary cannot flicker the column. */
const EXPAND_HYSTERESIS = 40
/** Past this much drift from its own paragraph, a card no longer reads as belonging to it. */
const MAX_CARD_DISPLACEMENT = 160

/**
 * The height of one line of a block, in px.
 *
 * `getComputedStyle(...).lineHeight` returns the string `normal` when nothing sets it explicitly,
 * which parses to NaN — so this falls back to a multiple of the font size rather than to the
 * block's own height. Falling back to the block height is what put the hover "+" beside the last
 * line of a four-line paragraph instead of the first.
 */
function firstLineHeightOf(block: HTMLElement): number {
  const computed = window.getComputedStyle(block)
  const lineHeight = Number.parseFloat(computed.lineHeight)
  if (Number.isFinite(lineHeight)) return lineHeight
  const fontSize = Number.parseFloat(computed.fontSize)
  return Number.isFinite(fontSize) ? fontSize * 1.5 : 24
}

interface AnchoredPosition {
  anchorId: string
  block: HTMLElement
  /** 1-based paragraph number among all anchorable blocks, for the accessible name. */
  paragraphNumber: number
  preview: string
  top: number
  /**
   * The block's own box, relative to the scroll region, measured in the same pass as `top`.
   *
   * The highlight used to derive its box from `proseLeft` and a hover-time `offsetWidth`/
   * `offsetHeight` read — two different coordinate frames, one of them read from a node that may
   * have been replaced since it was measured. Measuring the block once, here, keeps the rectangle
   * in the same frame as every other position this component emits.
   */
  left: number
  width: number
  height: number
  /**
   * How many lines the anchored paragraph occupies. The card clamps to this + 1, so a note is
   * never more than a line longer than the prose it annotates — the rest is in the dialog.
   */
  lineCount: number
}

interface HoverTarget {
  block: HTMLElement
  top: number
  /** The block's own top edge, before the first-line centring `top` carries. Stacked placement
      measures from the block, not from a glyph already nudged down onto its first line. */
  blockTop: number
}

export interface ParagraphNoteMarginProps {
  editorRef: React.RefObject<HTMLDivElement | null>
  scrollRef: React.RefObject<HTMLElement | null>
  notes: ParagraphNote[]
  /**
   * A STRUCTURAL key over the manuscript (block count + anchor set — see anchorStructureKey), not
   * the manuscript HTML itself. Drives a re-measure. Passing raw content here would put a full
   * measurement pass on every keystroke, which design spec §6.4 rules out: each pass rebuilds a
   * ResizeObserver, re-registers document.fonts.ready and forces a layout read per anchored block.
   */
  contentVersion: string
  onAddNote: (anchorId: string, block: HTMLElement) => void
  onOpenNote: (note: ParagraphNote) => void
}

export default function ParagraphNoteMargin({
  editorRef,
  scrollRef,
  notes,
  contentVersion,
  onAddNote,
  onOpenNote
}: ParagraphNoteMarginProps): React.JSX.Element | null {
  const [positions, setPositions] = useState<AnchoredPosition[]>([])
  const [proseLeft, setProseLeft] = useState(0)
  // Read inside the native mousemove listener; synced in an effect for the same react-hooks/refs
  // reason as the other refs here.
  const proseLeftRef = useRef(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [hoverTarget, setHoverTarget] = useState<HoverTarget | null>(null)
  // Read inside the native mousemove listener, which is registered once and would otherwise close
  // over a stale `hoverTarget` from the render that installed it. Synced in an effect for the same
  // react-hooks/refs reason as notesRef below.
  const hoverTargetRef = useRef<HoverTarget | null>(null)
  const [blockTops, setBlockTops] = useState<number[]>([])
  const [highlighted, setHighlighted] = useState<AnchoredPosition | null>(null)
  const [collapsedAnchorIds, setCollapsedAnchorIds] = useState<Set<string>>(() => new Set())
  // Where the "N more notes" chip goes: just below the last VISIBLE card, so it reads as
  // continuing the column rather than sitting wherever CSS static-position happens to fall back
  // to (the overlay's top-left corner, with no `top`/`left` of its own).
  const [overflowTop, setOverflowTop] = useState(0)
  const frameRef = useRef<number | null>(null)
  const overlayRef = useRef<HTMLElement | null>(null)
  // measure() runs inside a rAF and must not be re-created on every notes change, so it reads the
  // current notes through a ref rather than closing over them. Synced in an effect, not written
  // directly during render — react-hooks/refs (the React Compiler's ref-mutation check) forbids
  // assigning to a ref's `.current` while rendering; an effect is the sanctioned place for it.
  const notesRef = useRef<ParagraphNote[]>(notes)

  const notesByAnchor = useMemo(() => {
    const map = new Map<string, ParagraphNote>()
    for (const note of notes) map.set(note.anchorId, note)
    return map
  }, [notes])

  // The hover handler is registered once (its effect deliberately depends only on the two refs, so
  // a notes change does not tear down and re-add a mousemove listener); it reads the current note
  // set through this ref instead. Synced in an effect for the same react-hooks/refs reason as
  // notesRef above.
  const notesByAnchorRef = useRef<Map<string, ParagraphNote>>(notesByAnchor)
  useEffect(() => {
    notesRef.current = notes
    notesByAnchorRef.current = notesByAnchor
  }, [notes, notesByAnchor])
  useEffect(() => {
    hoverTargetRef.current = hoverTarget
  }, [hoverTarget])
  useEffect(() => {
    proseLeftRef.current = proseLeft
  }, [proseLeft])

  const orphans = useMemo(() => notes.filter((note) => note.isOrphaned), [notes])
  // An orphan whose anchor is still physically present in the DOM (server marked it orphaned, but
  // the client hasn't re-measured, or the anchor briefly reappeared) would otherwise render TWICE
  // — once from `positions` (the normal, on-block marker) and again from `orphans` (the dashed,
  // off-block marker) — with two different accessible names for the same note.
  const positionedAnchorIds = useMemo(
    () => new Set(positions.map((position) => position.anchorId)),
    [positions]
  )

  const measure = useCallback((): void => {
    const editor = editorRef.current
    const scrollRegion = scrollRef.current
    if (!editor || !scrollRegion) return

    const scrollBounds = scrollRegion.getBoundingClientRect()
    const editorBounds = editor.getBoundingClientRect()
    // The prose text edge, relative to the scroll region: the editor's own left inset plus its
    // horizontal padding. Everything in the margin is anchored to this, so the column tracks the
    // text as the chat panel is dragged instead of drifting away from it.
    const paddingLeft = Number.parseFloat(getComputedStyle(editor).paddingLeft) || 0
    const measuredProseLeft = editorBounds.left - scrollBounds.left + paddingLeft
    setProseLeft(measuredProseLeft)

    setIsExpanded((current) => {
      const required = MINIMAP_CLEARANCE + CARD_WIDTH + CARD_GAP
      const threshold = current ? required - EXPAND_HYSTERESIS : required
      return measuredProseLeft >= threshold
    })

    // Only ANCHORED blocks are measured. Measuring every paragraph would put a full-document
    // getBoundingClientRect sweep on the typing path, on top of the one the minimap already runs.
    // The add button needs no pre-measurement — it is positioned from whichever block the pointer
    // is actually over (see the mouseover handler below).
    const blocks = scanAnchorableBlocks(editor)
    const measured: AnchoredPosition[] = []
    const tops: number[] = []
    // An orphan has no element to measure, so its stored anchor_position is the only coordinate it
    // has. Paragraph tops are collected only when at least one orphan exists — a rare state — so
    // the common path stays a sweep over anchored blocks alone.
    const needsBlockTops = notesRef.current.some((note) => note.isOrphaned)

    blocks.forEach((block, index) => {
      const anchorId = anchorIdOf(block)
      const top =
        needsBlockTops || anchorId
          ? block.getBoundingClientRect().top - scrollBounds.top + scrollRegion.scrollTop
          : 0
      if (needsBlockTops) tops.push(top)
      if (!anchorId) return
      const rect = block.getBoundingClientRect()
      measured.push({
        anchorId,
        block,
        paragraphNumber: index + 1,
        preview: (block.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60),
        top,
        left: rect.left - scrollBounds.left,
        width: rect.width,
        height: rect.height,
        lineCount: Math.max(1, Math.round(rect.height / firstLineHeightOf(block)))
      })
    })
    setPositions(measured)
    setBlockTops(tops)
  }, [editorRef, scrollRef])

  const scheduleMeasure = useCallback((): void => {
    if (frameRef.current !== null) return
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null
      measure()
    })
  }, [measure])

  useEffect(() => {
    scheduleMeasure()
    const scrollRegion = scrollRef.current
    if (!scrollRegion) return
    const observer = new ResizeObserver(scheduleMeasure)
    observer.observe(scrollRegion)
    if (editorRef.current) observer.observe(editorRef.current)
    // Caveat loads with display=swap, so the first paint uses a fallback with different metrics
    // and every card's height changes when the real face arrives — after positions were computed.
    void document.fonts?.ready.then(scheduleMeasure)
    return () => {
      observer.disconnect()
      // Resetting frameRef.current is not optional: the rAF callback below is the only other
      // place that clears it, and it was just cancelled, so without this line
      // scheduleMeasure's `if (frameRef.current !== null) return` guard latches true forever —
      // no frame is ever scheduled again. StrictMode's dev-only double-invoke (schedule, cancel
      // via this cleanup, re-run the effect) reproduces this on every mount; in production the
      // same latch trips the first time this effect re-runs while a frame is still pending.
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
    // `notes` is included so an orphan flip (or any other notes-only change with no matching HTML
    // change) forces a re-measure — otherwise a newly-orphaned note's marker would render at
    // whatever blockTops happened to hold from the last HTML-driven measurement, or `?? 0`.
  }, [editorRef, scrollRef, scheduleMeasure, contentVersion, notes])

  // One add button, positioned from whatever the pointer is over. Tracking the hovered LINE
  // rather than the paragraph's first line matters: a ten-line paragraph is ~331px tall, so a
  // button pinned to its top would materialise far from the cursor and the diagonal travel to
  // reach it would leave the paragraph.
  useEffect(() => {
    const editor = editorRef.current
    const scrollRegion = scrollRef.current
    if (!editor || !scrollRegion) return

    const handleMove = (event: MouseEvent): void => {
      const element = event.target instanceof HTMLElement ? event.target : null
      const block = element?.closest<HTMLElement>(MANUSCRIPT_PARAGRAPH_SELECTOR)

      // Over something that cannot take a note. Two very different situations share this branch:
      //
      //  - The pointer is in the gutter corridor, left of the prose. The glyph lives inside the
      //    editor's own 64px left padding, which is part of the editor's hit region, so a pointer
      //    travelling toward the button samples `event.target === editor` on the way in — and
      //    mouseleave never fires to compensate, because the pointer never leaves the editor.
      //    Clearing here would unmount the button out from under the click. Keep the target.
      //
      //  - The pointer is over prose that is not an anchorable block — most often a bare text node,
      //    which real pasted manuscripts are full of. Keeping the target there parks a stale "+"
      //    beside a paragraph the pointer is nowhere near, which reads as the button being aligned
      //    to the wrong line. Clear it.
      // Clearing requires POSITIVE evidence that the pointer is over prose: a measured prose edge,
      // and a pointer to the right of it. When the geometry is unknown (proseLeft still 0 — before
      // the first measure, and always under jsdom, where every rect is zero) this falls back to
      // keeping the target, because the cost of being wrong is asymmetric. Wrongly keeping it
      // leaves a stale button; wrongly clearing it unmounts the button mid-click and makes the
      // affordance unreachable by a real pointer, which is the failure this handler exists to
      // prevent.
      const pointerX = event.clientX - scrollRegion.getBoundingClientRect().left
      const isOverProse = proseLeftRef.current > 0 && pointerX >= proseLeftRef.current
      if (!block || block === editor || !editor.contains(block) || !isAnchorableBlock(block)) {
        if (isOverProse) setHoverTarget(null)
        return
      }

      // Keyed on the NOTE SET, not on the presence of the attribute: a `data-note` attribute alone
      // is not evidence that a note exists. The case that proves it is undo-after-delete — deleting
      // a note strips the anchor and removes the row but records no history entry, so Cmd+Z restores
      // the anchor while the row stays gone. A failed save and a chapter-revision restore leave the
      // same residue. Trusting the attribute there would render no marker (positions.map returns
      // null with no note) AND withhold the "+", locking the paragraph out of annotation forever.
      const anchorId = anchorIdOf(block)
      if (anchorId && notesByAnchorRef.current.has(anchorId)) {
        // The marker already owns this slot; two affordances in one place is one too many. This
        // DOES clear (not just "does nothing"): moving from an unanchored paragraph directly onto
        // an anchored one must drop the "+" immediately, not leave it stuck over the old block.
        setHoverTarget(null)
        return
      }
      // Pinned to the paragraph's FIRST LINE, not to the pointer. Tracking the pointer's y kept the
      // button in easy reach on a tall paragraph, but it made the glyph slide continuously as the
      // mouse moved, which reads as jitter rather than as an affordance belonging to the paragraph.
      // It also means the position only changes when the hovered BLOCK changes, so the common case
      // — moving within one paragraph — costs no layout read and no state update at all.
      if (hoverTargetRef.current?.block === block) return

      const scrollBounds = scrollRegion.getBoundingClientRect()
      const blockBounds = block.getBoundingClientRect()
      const blockTop = blockBounds.top - scrollBounds.top + scrollRegion.scrollTop
      // Centred on the FIRST line rather than flush with the block's top edge: a line box is taller
      // than the 20px glyph, so aligning the boxes would sit the button noticeably above the text.
      //
      // Never fall back to the block's own height here. `line-height: normal` parses to NaN, and an
      // earlier version used `|| blockBounds.height`, which centred the glyph on the whole
      // paragraph — on a four-line block that put it beside the LAST line.
      setHoverTarget({
        block,
        top: blockTop + Math.max((firstLineHeightOf(block) - GLYPH_WIDTH) / 2, 0),
        blockTop
      })
    }
    const handleLeave = (event: MouseEvent): void => {
      // Do NOT delete this guard: it covers the final editor -> glyph transition, which happens on
      // every real click of the "+". The button is a child of the overlay <aside>, not of the
      // editor, so the moment the pointer crosses onto it the editor fires mouseleave with the
      // glyph as relatedTarget — even though the glyph is painted over the editor's own padding
      // (in the gutter it sits inside the editor's left padding; stacked above the paragraph it
      // sits in the band between two blocks, which is inside the editor too, so the "renders
      // outside the editor's box" geometry this comment used to describe cannot actually occur).
      // Clearing here would unmount the button out from under the click.
      const next = event.relatedTarget
      if (next instanceof Node && overlayRef.current?.contains(next)) return
      setHoverTarget(null)
    }
    const handleOverlayLeave = (event: MouseEvent): void => {
      // handleMove no longer clears on its own, so something must: this fires when the pointer
      // leaves the overlay (the glyph, ordinarily) for anywhere that ISN'T the editor — e.g. off
      // the button into empty page, or onto the sidebar. Leaving the overlay back INTO the editor
      // (the common case, since the glyph sits over the editor's own padding) must not clear —
      // handleMove will immediately re-evaluate that new position on the next mousemove sample.
      const next = event.relatedTarget
      if (next instanceof Node && editor.contains(next)) return
      setHoverTarget(null)
    }

    // Touch has no hover, so this "+" glyph never appears on a touch device — deliberately, not
    // an oversight. Existing notes stay reachable on touch regardless, because markers (below)
    // render unconditionally rather than only on hover; creating a NEW note on touch goes through
    // the Cmd/Ctrl+Alt+M keyboard shortcut instead (StoryCanvas's handleEditorKeyDown), via an
    // external keyboard or a desktop session. Do not "fix" this by adding a touchstart/tap handler
    // here — a tap already places the caret through the OS's own text-selection gesture, and a
    // second handler racing it to show/hide the add button would fight that caret placement.
    editor.addEventListener('mousemove', handleMove)
    editor.addEventListener('mouseleave', handleLeave)
    // overlayRef.current may be null on this first effect run if the aside hasn't attached its
    // ref yet — it renders unconditionally so it should be present, but this doesn't assume it.
    const overlay = overlayRef.current
    overlay?.addEventListener('mouseleave', handleOverlayLeave)
    return () => {
      editor.removeEventListener('mousemove', handleMove)
      editor.removeEventListener('mouseleave', handleLeave)
      overlay?.removeEventListener('mouseleave', handleOverlayLeave)
    }
  }, [editorRef, scrollRef])

  // Cards are taller than the paragraphs they annotate — prose pitch is ~53px per line, and a
  // 200px card of two wrapped lines already exceeds it — so on dialogue-heavy pages they would
  // overlap. Push each card down to clear the one above it, in document order. Past a budget of
  // drift from its own paragraph, a card no longer reads as belonging to it, so it collapses into
  // a count instead of continuing to cascade.
  useEffect(() => {
    if (!isExpanded) return
    const layer = overlayRef.current
    if (!layer) return
    const cards = Array.from(layer.querySelectorAll<HTMLElement>('.paragraph-note-card'))
    let previousBottom = -Infinity
    const collapsed = new Set<string>()
    for (const card of cards) {
      const anchorTop = Number.parseFloat(card.dataset.anchorTop || '0')
      const top = Math.max(anchorTop, previousBottom + 8)
      const displacement = top - anchorTop
      if (displacement > MAX_CARD_DISPLACEMENT) {
        card.style.display = 'none'
        if (card.dataset.anchorId) collapsed.add(card.dataset.anchorId)
        continue
      }
      card.style.display = ''
      card.style.transform = `translateY(${displacement}px)`
      // Only a card whose text is actually cut gets the hover-to-read treatment. Measured here
      // because this pass already runs after layout, and the answer depends on the clamp having
      // been applied. scrollHeight exceeds clientHeight exactly when the -webkit-box clipped
      // something.
      const cardBody = card.querySelector<HTMLElement>('.paragraph-note-card-body')
      const isClamped = Boolean(cardBody && cardBody.scrollHeight > cardBody.clientHeight + 1)
      card.dataset.clamped = isClamped ? 'true' : 'false'
      previousBottom = top + card.offsetHeight
    }
    // Which anchors collapsed, not just how many: a hidden card leaves its note with no control at
    // all in expanded mode (the marker is not rendered alongside a card, and the overflow chip is
    // aria-hidden), so those notes get their marker back. Tracked as ids rather than a count
    // purely so the render below can ask "did THIS one collapse?".
    setCollapsedAnchorIds((previous) =>
      previous.size === collapsed.size && [...collapsed].every((id) => previous.has(id))
        ? previous
        : collapsed
    )
    // previousBottom is -Infinity only when there were no cards to begin with, in which case
    // collapsedCount is also 0 and the chip never renders — but guard the arithmetic anyway
    // rather than let a stray -Infinity leak into an inline style.
    setOverflowTop(Number.isFinite(previousBottom) ? previousBottom + 8 : 0)
  }, [isExpanded, positions, notes])

  // On a phone there is no gutter to put a glyph in. The editor's left padding falls to 20px, so
  // the position this wants is proseLeft - 26 = -6, and the MINIMAP_CLEARANCE clamp below turned
  // that into 42 -- which is 22px INSIDE the prose, printing the "+" and the note markers on top
  // of the first words of the paragraph they belong to.
  //
  // Where there is no room beside the paragraph, the glyph goes above it instead: aligned to the
  // text's own left edge, sitting in the 20px band between one paragraph and the next. The band is
  // exactly the glyph's height, so it overlaps neither the paragraph it marks nor the one above.
  const hasGutter = proseLeft - GLYPH_GAP - GLYPH_WIDTH >= MINIMAP_CLEARANCE
  // Clamped to MINIMAP_CLEARANCE, not 0: the minimap rail owns x in [10, 34] and its own glyphs
  // are pointer-events: auto, so a glyph left at x=0 would sit under the rail and steal its click.
  const glyphLeft = hasGutter
    ? Math.max(proseLeft - GLYPH_GAP - GLYPH_WIDTH, MINIMAP_CLEARANCE)
    : proseLeft
  /** Glyph top when it is stacked above its paragraph rather than set beside it. */
  const stackedTop = (blockTop: number): number => Math.max(blockTop - GLYPH_WIDTH, 0)
  // Same clamp, same reason: in the hysteresis band (proseLeft can be as low as
  // MINIMAP_CLEARANCE + CARD_WIDTH + CARD_GAP - EXPAND_HYSTERESIS = 234 while still expanded), a
  // bare 0 floor would put the card at left: 2px — underneath the minimap rail this constant
  // exists to clear.
  const cardLeft = Math.max(proseLeft - CARD_GAP - CARD_WIDTH, MINIMAP_CLEARANCE)

  return (
    <aside
      ref={overlayRef}
      className={`paragraph-note-margin${isExpanded ? ' is-expanded' : ''}`}
      aria-label="Margin notes"
    >
      {highlighted && (
        <div
          className="paragraph-note-highlight"
          aria-hidden="true"
          style={{
            top: `${highlighted.top}px`,
            left: `${highlighted.left}px`,
            width: `${highlighted.width}px`,
            height: `${highlighted.height}px`
          }}
        />
      )}
      {hoverTarget && (
        <button
          type="button"
          className="paragraph-note-add"
          style={{
            top: `${hasGutter ? hoverTarget.top : stackedTop(hoverTarget.blockTop)}px`,
            left: `${glyphLeft}px`
          }}
          aria-label="Add a note to this paragraph"
          onClick={() => onAddNote(createAnchorId(), hoverTarget.block)}
        >
          <Plus size={12} aria-hidden="true" />
          <span className="paragraph-note-add-label" aria-hidden="true">
            Add a note
          </span>
        </button>
      )}
      {positions.map((position) => {
        const note = notesByAnchor.get(position.anchorId)
        if (!note) return null
        // Three states, not two. A footnote is the one note kind readers can see, so showing it
        // with the same glyph and the same words as an ordinary agent-visible note would hide the
        // only thing that makes it different.
        const visibility = note.isFootnote
          ? 'published as a footnote'
          : note.isAgentContext
            ? 'visible to the writing agent'
            : 'private'
        const visibilityIcon = note.isFootnote ? (
          <Asterisk size={12} aria-hidden="true" />
        ) : note.isAgentContext ? (
          <Sparkles size={12} aria-hidden="true" />
        ) : (
          <EyeOff size={12} aria-hidden="true" />
        )

        const label = `Note on paragraph ${position.paragraphNumber}: ${position.preview} — ${visibility}`

        return (
          <React.Fragment key={note.id}>
            {/* Marker OR card, never both. When there is room for the note itself, the glyph is
                redundant decoration beside text that already says everything it said. The two
                therefore swap roles rather than stack: whichever renders is the real control,
                carrying the accessible name and the focus. */}
            {(!isExpanded || collapsedAnchorIds.has(position.anchorId)) && (
              <button
                type="button"
                className="paragraph-note-marker"
                style={{
                  top: `${hasGutter ? position.top : stackedTop(position.top)}px`,
                  left: `${glyphLeft}px`
                }}
                aria-label={label}
                onMouseEnter={() => setHighlighted(position)}
                onMouseLeave={() => setHighlighted(null)}
                onFocus={() => setHighlighted(position)}
                onBlur={() => setHighlighted(null)}
                onClick={() => onOpenNote(note)}
              >
                {visibilityIcon}
              </button>
            )}
            {isExpanded && (
              <button
                type="button"
                className="paragraph-note-card"
                data-anchor-top={position.top}
                data-anchor-id={position.anchorId}
                style={{
                  top: `${position.top}px`,
                  left: `${cardLeft}px`,
                  width: `${CARD_WIDTH}px`
                }}
                // The card is the ONLY control in expanded mode — the marker is not rendered
                // alongside it — so it carries the accessible name and stays in the tab order.
                // It was aria-hidden back when both rendered and the note would have been
                // announced twice.
                aria-label={label}
                onMouseEnter={() => setHighlighted(position)}
                onMouseLeave={() => setHighlighted(null)}
                onFocus={() => setHighlighted(position)}
                onBlur={() => setHighlighted(null)}
                onClick={() => onOpenNote(note)}
              >
                {/* The glyph the marker used to carry, now inline at the head of the note: with
                    the marker gone in expanded mode this was the only thing lost, and whether the
                    agent can read a note is exactly what a writer wants to see at a glance. */}
                <span className="paragraph-note-card-icon" aria-hidden="true">
                  {visibilityIcon}
                </span>
                <span
                  className="paragraph-note-card-body"
                  // Clamped to the paragraph's own height plus one line, so a long note never runs
                  // far past the prose it annotates and never cascades the cards below it out of
                  // position. Set as a custom property rather than -webkit-line-clamp directly:
                  // an inline value would outrank the :hover rule that lifts the clamp, and
                  // beating it would need !important.
                  style={{ ['--note-clamp' as string]: String(position.lineCount + 1) }}
                >
                  {note.body}
                </span>
              </button>
            )}
          </React.Fragment>
        )
      })}
      {orphans
        .filter((note) => !positionedAnchorIds.has(note.anchorId))
        .map((note) => (
          <button
            key={note.id}
            type="button"
            className="paragraph-note-marker is-orphaned"
            // anchor_position is a display hint and nothing more — every split and merge
            // renumbers the document, so it is never an identity or an ordering key.
            style={{
              top: `${(() => {
                const blockTop =
                  blockTops[Math.min(note.anchorPosition, Math.max(blockTops.length - 1, 0))] ?? 0
                return hasGutter ? blockTop : stackedTop(blockTop)
              })()}px`,
              left: `${glyphLeft}px`
            }}
            aria-label={`Note, paragraph changed: ${note.body.slice(0, 60)}`}
            onClick={() => onOpenNote(note)}
          >
            <PenLine size={12} aria-hidden="true" />
          </button>
        ))}
      {isExpanded && collapsedAnchorIds.size > 0 && (
        <span
          className="paragraph-note-card-overflow"
          aria-hidden="true"
          style={{ top: `${overflowTop}px`, left: `${cardLeft}px` }}
        >
          {collapsedAnchorIds.size} more {collapsedAnchorIds.size === 1 ? 'note' : 'notes'} in the
          margin
        </span>
      )}
    </aside>
  )
}
